import { useSyncExternalStore } from "react";
import { ApiError } from "@/lib/api-error";
import { touchSessionActivity } from "@/lib/session-activity";
import {
  pollBunnyUpload,
  uploadLessonVideoFile,
  VideoUploadAborted,
} from "@/services/api/video-upload";
import { useAuthStore } from "@/stores/authStore";

/**
 * CRS-2 / CRS-8: lesson video uploads and Bunny processing live here, outside any component, so
 * they survive switching builder steps or leaving the builder page; and they are re-adopted from
 * the server (builder GET → `videoUpload`) after a reload, so "processing" never exists only in a
 * browser tab.
 */
export type VideoJob =
  | {
      phase: "uploading";
      courseId: string;
      percent: number;
      fileName: string;
      uploadId?: string;
      /** SPA-3: paused while the browser is offline; carries on by itself. */
      waitingForNetwork?: boolean;
    }
  | { phase: "processing"; courseId: string; uploadId: string; since: string; pollFailures: number }
  /** Status checks failed MAX_POLL_FAILURES times in a row; polling stopped until retried. */
  | { phase: "stalled"; courseId: string; uploadId: string; since: string; error: unknown }
  /** The server has an unfinished upload that is not running in this tab: re-pick the file. */
  | {
      phase: "interrupted";
      courseId: string;
      uploadId: string;
      since: string;
      fileName: string | null;
    }
  | { phase: "failed"; courseId: string; uploadId?: string; error: unknown };

export type ServerVideoUpload = {
  id: string;
  status: "uploading" | "processing" | "failed";
  error: string | null;
  startedAt: string;
  fileName?: string | null;
};

export const POLL_INTERVAL_MS = 10_000;
export const MAX_POLL_FAILURES = 6;

let jobs: Readonly<Record<string, VideoJob>> = {};
const listeners = new Set<() => void>();
const readyListeners = new Set<(lessonId: string, courseId: string) => void>();
/** Failed uploads the instructor dismissed this session; the server keeps them for a day. */
const dismissed = new Set<string>();
let pollTimer: ReturnType<typeof setInterval> | null = null;
/** R-19: the transfers running in this tab, so signing out can stop them. */
const controllers = new Map<string, AbortController>();
let polling = false;

function emit() {
  for (const listener of listeners) listener();
}

function setJob(lessonId: string, job: VideoJob | null) {
  const next = { ...jobs };
  if (job) next[lessonId] = job;
  else delete next[lessonId];
  jobs = next;
  emit();
  syncPoller();
  syncLeaveWarning();
}

export function getVideoJobs(): Readonly<Record<string, VideoJob>> {
  return jobs;
}

export function subscribeVideoJobs(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useVideoJobs(): Readonly<Record<string, VideoJob>> {
  return useSyncExternalStore(subscribeVideoJobs, getVideoJobs, getVideoJobs);
}

/** Called when a lesson's new video is live (or a local upload finished): reload the builder. */
export function onLessonVideoReady(
  listener: (lessonId: string, courseId: string) => void,
): () => void {
  readyListeners.add(listener);
  return () => readyListeners.delete(listener);
}

function notifyReady(lessonId: string, courseId: string) {
  for (const listener of readyListeners) listener(lessonId, courseId);
}

/** Upload/Replace is disabled while the lesson is uploading or Bunny is still processing it. */
export function lessonVideoBusy(job: VideoJob | undefined): boolean {
  return job?.phase === "uploading" || job?.phase === "processing" || job?.phase === "stalled";
}

export function anyUploadRunning(): boolean {
  return Object.values(jobs).some((job) => job.phase === "uploading");
}

export async function startLessonVideoUpload(
  courseId: string,
  lessonId: string,
  file: File,
): Promise<void> {
  if (lessonVideoBusy(jobs[lessonId])) return;
  setJob(lessonId, { phase: "uploading", courseId, percent: 0, fileName: file.name });
  const patchUploading = (patch: {
    percent?: number;
    uploadId?: string;
    waitingForNetwork?: boolean;
  }) => {
    const current = jobs[lessonId];
    if (current?.phase === "uploading") setJob(lessonId, { ...current, ...patch });
  };
  const controller = new AbortController();
  controllers.set(lessonId, controller);
  try {
    const result = await uploadLessonVideoFile(lessonId, file, {
      onProgress: (percent) => patchUploading({ percent }),
      onTicket: (uploadId) => patchUploading({ uploadId }),
      onWaitingForNetwork: (waitingForNetwork) => patchUploading({ waitingForNetwork }),
      signal: controller.signal,
    });
    if (result.mode === "local") {
      setJob(lessonId, null);
      notifyReady(lessonId, courseId);
      return;
    }
    setJob(lessonId, {
      phase: "processing",
      courseId,
      uploadId: result.uploadId,
      since: new Date().toISOString(),
      pollFailures: 0,
    });
  } catch (error) {
    if (error instanceof VideoUploadAborted) {
      if (jobs[lessonId]?.phase === "uploading") setJob(lessonId, null);
      return;
    }
    setJob(lessonId, { phase: "failed", courseId, uploadId: jobUploadId(lessonId), error });
  } finally {
    if (controllers.get(lessonId) === controller) controllers.delete(lessonId);
  }
}

/**
 * R-19: stop every transfer running in this tab, including one paused for the network — a later
 * `online` event must not restart it. The server keeps the unfinished upload; re-picking the
 * same file resumes it (CRS-8).
 */
export function cancelVideoUploads(): void {
  for (const controller of controllers.values()) controller.abort();
  controllers.clear();
}

// Signing out abandons the transfers of the signed-out user.
useAuthStore.subscribe((state, previous) => {
  if (previous.user && !state.user) cancelVideoUploads();
});

function jobUploadId(lessonId: string): string | undefined {
  const job = jobs[lessonId];
  return job && "uploadId" in job ? job.uploadId : undefined;
}

/** A stored job error (a code such as ENCODE_FAILED) as something apiErrorText can translate. */
export function uploadJobError(code: string | null): ApiError {
  return new ApiError(code ?? "ENCODE_FAILED", "", null);
}

/**
 * Re-adopt what the server knows after the builder (re)loads. A transfer running in this tab
 * wins over the server's view of it; everything else follows the server.
 */
export function adoptServerUploads(
  courseId: string,
  lessons: ReadonlyArray<{ id: string; videoUpload?: ServerVideoUpload | null }>,
): void {
  for (const lesson of lessons) {
    const current = jobs[lesson.id];
    if (current?.phase === "uploading") continue;
    const upload = lesson.videoUpload;
    if (!upload || dismissed.has(upload.id)) {
      if (current && current.courseId === courseId) setJob(lesson.id, null);
      continue;
    }
    if (upload.status === "processing") {
      if (
        (current?.phase === "processing" || current?.phase === "stalled") &&
        current.uploadId === upload.id
      ) {
        continue;
      }
      setJob(lesson.id, {
        phase: "processing",
        courseId,
        uploadId: upload.id,
        since: upload.startedAt,
        pollFailures: 0,
      });
    } else if (upload.status === "uploading") {
      setJob(lesson.id, {
        phase: "interrupted",
        courseId,
        uploadId: upload.id,
        since: upload.startedAt,
        fileName: upload.fileName ?? null,
      });
    } else {
      setJob(lesson.id, {
        phase: "failed",
        courseId,
        uploadId: upload.id,
        error: uploadJobError(upload.error),
      });
    }
  }
}

/** "Check again" after polling gave up. */
export function retryVideoStatus(lessonId: string): void {
  const job = jobs[lessonId];
  if (job?.phase !== "stalled") return;
  setJob(lessonId, { ...job, phase: "processing", pollFailures: 0 });
  void pollOnce();
}

/** Hide a failed upload's message for the rest of this session. */
export function dismissVideoJob(lessonId: string): void {
  const uploadId = jobUploadId(lessonId);
  if (uploadId) dismissed.add(uploadId);
  setJob(lessonId, null);
}

async function pollOnce(): Promise<void> {
  if (polling) return;
  polling = true;
  try {
    for (const [lessonId, job] of Object.entries(jobs)) {
      if (job.phase !== "processing") continue;
      // An instructor waiting on a 2-hour encode is not idle.
      touchSessionActivity();
      try {
        const status = await pollBunnyUpload(lessonId, job.uploadId);
        const current = jobs[lessonId];
        if (current?.phase !== "processing" || current.uploadId !== job.uploadId) continue;
        if (status.status === "ready") {
          setJob(lessonId, null);
          notifyReady(lessonId, job.courseId);
        } else if (status.status === "failed") {
          setJob(lessonId, {
            phase: "failed",
            courseId: job.courseId,
            uploadId: job.uploadId,
            error: uploadJobError(status.error),
          });
        } else if (current.pollFailures > 0) {
          setJob(lessonId, { ...current, pollFailures: 0 });
        }
      } catch (error) {
        const current = jobs[lessonId];
        if (current?.phase !== "processing") continue;
        const failures = current.pollFailures + 1;
        setJob(
          lessonId,
          failures >= MAX_POLL_FAILURES
            ? {
                phase: "stalled",
                courseId: current.courseId,
                uploadId: current.uploadId,
                since: current.since,
                error,
              }
            : { ...current, pollFailures: failures },
        );
      }
    }
  } finally {
    polling = false;
  }
}

function syncPoller() {
  const needed = Object.values(jobs).some((job) => job.phase === "processing");
  if (needed && !pollTimer) {
    pollTimer = setInterval(() => void pollOnce(), POLL_INTERVAL_MS);
    void Promise.resolve().then(pollOnce);
  } else if (!needed && pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function warnBeforeLeaving(event: BeforeUnloadEvent) {
  event.preventDefault();
  // Chrome needs returnValue set; the text itself is never shown.
  event.returnValue = "";
}
let warningInstalled = false;

/** CRS-8: closing or reloading the tab mid-transfer asks first. */
function syncLeaveWarning() {
  if (typeof window === "undefined") return;
  const needed = anyUploadRunning();
  if (needed && !warningInstalled) {
    window.addEventListener("beforeunload", warnBeforeLeaving);
    warningInstalled = true;
  } else if (!needed && warningInstalled) {
    window.removeEventListener("beforeunload", warnBeforeLeaving);
    warningInstalled = false;
  }
}

/** Tests only: module state outlives a test. */
export function resetVideoJobsForTests(): void {
  cancelVideoUploads();
  jobs = {};
  dismissed.clear();
  readyListeners.clear();
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
  polling = false;
  syncLeaveWarning();
  emit();
}
